export const cropImageToCenterRange = (file: File, options: { width: number; height: number }): Promise<File> => {
	return new Promise((resolve, reject) => {
		const img = new Image();
		const reader = new FileReader();
		const { width, height } = options;

		reader.onload = (e) => {
			img.src = e.target?.result as string;

			img.onload = () => {
				const canvas = document.createElement("canvas");
				const ctx = canvas.getContext("2d");

				if (!ctx) {
					reject("Failed to get canvas context");
					return;
				}

				const imgWidth = img.width;
				const imgHeight = img.height;

				// Calculate scale to fit the image within the 200x200 box
				// we want the scale that gets as much of the image as possible, while maintining a filled background
				const scale = Math.max(width / imgWidth, height / imgHeight);
				const scaledWidth = imgWidth * scale;
				const scaledHeight = imgHeight * scale;

				// Calculate the top-left corner to center the scaled image in the 200x200 box
				// this is so we grab the middle 200x200 square of the scaled image
				const offsetX = (width - scaledWidth) / 2;
				const offsetY = (height - scaledHeight) / 2;

				canvas.width = width;
				canvas.height = height;

				ctx.drawImage(img, offsetX, offsetY, scaledWidth, scaledHeight);

				canvas.toBlob((blob) => {
					if (blob) {
						const newFile = new File([blob], "cropped_image.png", {
							type: "image/png",
						});
						resolve(newFile);
					} else {
						reject("Failed to create blob from canvas");
					}
				}, "image/png");
			};

			img.onerror = () => {
				reject("Failed to load image");
			};
		};

		reader.onerror = () => {
			reject("Failed to read file");
		};

		reader.readAsDataURL(file);
	});
};

export const scaleImageToCanvas = (file: File, options?: { maxWidth: number; maxHeight: number }): Promise<File> => {
	return new Promise((resolve, reject) => {
		const img = new Image();
		const reader = new FileReader();

		reader.onload = (e) => {
			img.src = e.target?.result as string;

			img.onload = () => {
				const canvas = document.createElement("canvas");
				const ctx = canvas.getContext("2d");

				if (!ctx) {
					reject("Failed to get canvas context");
					return;
				}

				const imgWidth = img.width;
				const imgHeight = img.height;

				if (options?.maxHeight && options?.maxWidth) {
					const { maxWidth, maxHeight } = options;

					const scale = Math.max(maxWidth / imgWidth, maxHeight / imgHeight);
					const scaledWidth = imgWidth * scale;
					const scaledHeight = imgHeight * scale;

					canvas.width = scaledWidth;
					canvas.height = scaledHeight;
					ctx.drawImage(img, 0, 0, scaledWidth, scaledHeight);
				} else {
					canvas.width = imgWidth;
					canvas.height = imgHeight;
					ctx.drawImage(img, 0, 0, imgWidth, imgHeight);
				}

				canvas.toBlob((blob) => {
					if (blob) {
						const newFile = new File([blob], "cropped_image.png", {
							type: "image/png",
						});
						resolve(newFile);
					} else {
						reject("Failed to create blob from canvas");
					}
				}, "image/png");
			};

			img.onerror = () => {
				reject("Failed to load image");
			};
		};

		reader.onerror = () => {
			reject("Failed to read file");
		};

		reader.readAsDataURL(file);
	});
};

export const cropImageFromPixelCropAndHTMLImage = async (
	image: HTMLImageElement,
	crop: { width: number; height: number; x: number; y: number },
	scale = 1
): Promise<File | null> => {
	return new Promise((resolve, reject) => {
		const canvas = document.createElement("canvas");
		const ctx = canvas.getContext("2d");

		if (!ctx) {
			throw new Error("No 2d context");
		}

		const scaleX = image.naturalWidth / image.width;
		const scaleY = image.naturalHeight / image.height;
		// devicePixelRatio slightly increases sharpness on retina devices
		// at the expense of slightly slower render times and needing to
		// size the image back down if you want to download/upload and be
		// true to the images natural size.
		const pixelRatio = window.devicePixelRatio;

		canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
		canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

		ctx.scale(pixelRatio, pixelRatio);
		ctx.imageSmoothingQuality = "high";

		const cropX = crop.x * scaleX;
		const cropY = crop.y * scaleY;

		const centerX = image.naturalWidth / 2;
		const centerY = image.naturalHeight / 2;

		ctx.save();

		// 5) Move the crop origin to the canvas origin (0,0)
		ctx.translate(-cropX, -cropY);
		// 4) Move the origin to the center of the original position
		ctx.translate(centerX, centerY);
		// 3) Rotate around the origin, not implemented since it is not needed yet. If we needed to handle rotation we would handle it here
		// 2) Scale the image
		ctx.scale(scale, scale);
		// 1) Move the center of the image to the origin (0,0)
		ctx.translate(-centerX, -centerY);
		ctx.drawImage(
			image,
			0,
			0,
			image.naturalWidth,
			image.naturalHeight,
			0,
			0,
			image.naturalWidth,
			image.naturalHeight
		);

		canvas.toBlob((blob) => {
			if (blob) {
				const newFile = new File([blob], "cropped_image.png", {
					type: "image/png",
				});
				resolve(newFile);
			} else {
				reject("Failed to create blob from canvas");
			}
		}, "image/png");

		ctx.restore();
	});
};
